/**
 * Permission helper functions for common permission checks
 * Centralizes permission logic that was previously scattered across components
 */

/**
 * Check if user can edit a vehicle
 * @param currentAccount - Current account object
 * @param currentUser - Current user object
 * @returns boolean indicating if user can edit vehicles
 */
export const canEditVehicle = (
  currentAccount: Account | undefined,
  currentUser: User | undefined
): boolean => {
  if (!currentAccount || !currentUser) {
    return false;
  }

  // Super admins can always edit
  if (currentUser.role?.id === 1) {
    return true;
  }

  // Check account-level permissions for vehicles
  return !!currentAccount?.settings?.data?.vehicles?.sellers_can_edit_vehicles?.value;
};

/**
 * Check if user can delete a vehicle
 * @param currentAccount - Current account object
 * @param currentUser - Current user object
 * @returns boolean indicating if user can delete vehicles
 */
export const canDeleteVehicle = (
  currentAccount: Account | undefined,
  currentUser: User | undefined
): boolean => {
  if (!currentAccount || !currentUser) {
    return false;
  }

  // Super admins can always delete
  if (currentUser.role?.id === 1) {
    return true;
  }

  // Check account-level permissions for vehicles
  return !!currentAccount?.settings?.data?.vehicles?.sellers_can_edit_vehicles?.value;
};

/**
 * Check if user can manage deals
 * @param currentAccount - Current account object
 * @param currentUser - Current user object
 * @returns boolean indicating if user can manage deals
 */
export const canManageDeals = (
  currentAccount: Account | undefined,
  currentUser: User | undefined
): boolean => {
  if (!currentAccount || !currentUser) {
    return false;
  }

  // Super admins can always manage deals
  if (currentUser.role?.id === 1) {
    return true;
  }

  // Check account-level permissions
  return !!currentAccount?.settings?.data?.dealer?.deals?.value;
};

/**
 * Check if user can manage contacts
 * @param currentAccount - Current account object
 * @param currentUser - Current user object
 * @returns boolean indicating if user can manage contacts
 */
export const canManageContacts = (
  currentAccount: Account | undefined,
  currentUser: User | undefined
): boolean => {
  if (!currentAccount || !currentUser) {
    return false;
  }

  // Super admins can always manage contacts
  if (currentUser.role?.id === 1) {
    return true;
  }

  // Check account-level permissions
  return !!currentAccount?.settings?.data?.contacts?.list?.value;
};

/**
 * Check if user can manage pricing
 * @param currentAccount - Current account object
 * @param currentUser - Current user object
 * @returns boolean indicating if user can manage pricing
 */
export const canManagePricing = (
  currentAccount: Account | undefined,
  currentUser: User | undefined
): boolean => {
  if (!currentAccount || !currentUser) {
    return false;
  }

  // Super admins can always manage pricing
  if (currentUser.role?.id === 1) {
    return true;
  }

  // Check account-level permissions
  return !!currentAccount?.settings?.data?.dealer?.pricing?.value;
};

/**
 * Check if user can manage account settings
 * @param currentAccount - Current account object
 * @param currentUser - Current user object
 * @returns boolean indicating if user can manage account settings
 */
export const canManageAccountSettings = (
  currentAccount: Account | undefined,
  currentUser: User | undefined
): boolean => {
  if (!currentAccount || !currentUser) {
    return false;
  }

  // Super admins can always manage settings
  if (currentUser.role?.id === 1) {
    return true;
  }

  // Only account admins can manage settings
  return currentUser.role?.id === 2; // ADMIN role
};

/**
 * Check if user can manage users
 * @param currentAccount - Current account object
 * @param currentUser - Current user object
 * @returns boolean indicating if user can manage users
 */
export const canManageUsers = (
  currentAccount: Account | undefined,
  currentUser: User | undefined
): boolean => {
  if (!currentAccount || !currentUser) {
    return false;
  }

  // Super admins can always manage users
  if (currentUser.role?.id === 1) {
    return true;
  }

  // Only account admins can manage users
  return currentUser.role?.id === 2; // ADMIN role
};

/**
 * Check if account has vehicle AI image processing enabled
 * @param currentAccount - Current account object
 * @returns boolean indicating if image processing is active
 */
export const isVehicleAiImageProcessingEnabled = (
  currentAccount: Account | undefined
): boolean => {
  return currentAccount?.settings?.data?.vehicles?.ai?.value?.image_processing?.active === true;
};

/**
 * Check if account has vehicle AI text processing enabled
 * @param currentAccount - Current account object
 * @returns boolean indicating if text processing is active
 */
export const isVehicleAiTextProcessingEnabled = (
  currentAccount: Account | undefined
): boolean => {
  return currentAccount?.settings?.data?.vehicles?.ai?.value?.text_processing?.active === true;
};

/**
 * Check if user is a super admin
 * @param currentUser - Current user object
 * @returns boolean indicating if user is a super admin
 */
export const isSuperAdmin = (currentUser: User | undefined): boolean => {
  return currentUser?.role?.id === 1;
};

/**
 * Check if user is an account admin
 * @param currentUser - Current user object
 * @returns boolean indicating if user is an account admin
 */
export const isAccountAdmin = (currentUser: User | undefined): boolean => {
  return currentUser?.role?.id === 2;
};
